import React from "react";
import DateOfBirthField from "./DateOfBirthField";
import listStyles from "../styles/CustomFieldRenderer.module.css";

/**
 * Renders one form input for a product's dynamic field definition (from u_products.user_input_fields).
 * Numerology-only per 2026-10-08 decision — LandingPage.jsx keeps its own existing inline
 * text/textarea/dropdown switch untouched; this covers all 7 types (the 4 new ones —
 * checkbox/date/number/array — were added for the Product Edit page work alongside text/
 * textarea/dropdown).
 *
 * @param {object} field - field definition: {id, key, label, type, required, options,
 *   notFuture, minYear, min, max, maxItems, charsetValidation}
 * @param {*} value - current value for this field
 * @param {function} onChange - (newValue) => void
 * @param {string} [inputClassName] - className applied to text/textarea/number/date/array-item
 *   inputs — pass the caller's own `styles.formInput` so dynamic fields match its other inputs.
 * @param {string} [selectClassName] - className applied to the dropdown <select> — pass the
 *   caller's own `styles.formSelect`.
 */
// Mirrors backend-app/utils/customFieldValidator.js's charsetValidation check —
// strips disallowed characters live so the field can never hold a bad value,
// same pattern as admin-app's Field Key input.
function applyCharsetValidation(rawValue, charsetValidation) {
  if (charsetValidation === "alpha") return rawValue.replace(/[^A-Za-z ]/g, "");
  if (charsetValidation === "alphanumeric") return rawValue.replace(/[^A-Za-z0-9 ]/g, "");
  return rawValue;
}

export default function CustomFieldRenderer({ field, value, onChange, inputClassName = "", selectClassName = "" }) {
  const commonProps = {
    id: `field-${field.id}`,
    required: field.required,
  };

  switch (field.type) {
    case "text":
      return (
        <input
          {...commonProps}
          type="text"
          className={inputClassName}
          value={value || ""}
          maxLength={field.maxLength || 500}
          onChange={(e) => onChange(applyCharsetValidation(e.target.value, field.charsetValidation))}
        />
      );

    case "textarea":
      return (
        <textarea
          {...commonProps}
          className={inputClassName}
          value={value || ""}
          maxLength={field.maxLength || 500}
          onChange={(e) => onChange(applyCharsetValidation(e.target.value, field.charsetValidation))}
        />
      );

    case "dropdown":
      return (
        <select {...commonProps} className={selectClassName} value={value || ""} onChange={(e) => onChange(e.target.value)}>
          <option value="">Select...</option>
          {(field.options || []).map((opt) => (
            <option key={opt} value={opt}>
              {opt}
            </option>
          ))}
        </select>
      );

    case "checkbox":
      // Left-aligned checkbox + inline label (flex, align-items: center, gap: 8) —
      // the field's own label renders here instead of the separate stacked label
      // the caller shows for other field types.
      return (
        <label
          htmlFor={commonProps.id}
          style={{ display: "flex", alignItems: "center", gap: 8, fontWeight: 600, fontSize: 14, color: "#1f1f1f", cursor: "pointer", userSelect: "none" }}
        >
          <input
            {...commonProps}
            type="checkbox"
            checked={Boolean(value)}
            onChange={(e) => onChange(e.target.checked)}
            style={{ width: 16, height: 16, margin: 0, flexShrink: 0, cursor: "pointer" }}
          />
          <span>
            {field.label} {field.required && <span style={{ color: "#ff3d00" }}>*</span>}
          </span>
        </label>
      );

    case "date":
      // Same day/month/year dropdown UI as LandingPage.jsx's Date of Birth field.
      // notFuture/minYear come straight from the product's field config (set in
      // admin-app's ProductEdit.jsx custom field builder) and are enforced inside
      // DateOfBirthField itself — see its own notFuture/minYear props.
      return (
        <DateOfBirthField
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          required={field.required}
          notFuture={field.notFuture}
          minYear={field.minYear}
        />
      );

    case "number":
      return (
        <input
          {...commonProps}
          type="number"
          className={inputClassName}
          value={value ?? ""}
          min={field.min}
          max={field.max}
          onChange={(e) => onChange(e.target.value === "" ? "" : Number(e.target.value))}
        />
      );

    case "array": {
      const items = Array.isArray(value) ? value : [];
      const maxItems = field.maxItems || 5;
      const updateItem = (idx, itemValue) => {
        const next = [...items];
        next[idx] = itemValue;
        onChange(next.filter((v) => v !== undefined));
      };
      const addItem = () => {
        if (items.length < maxItems) onChange([...items, ""]);
      };
      const removeItem = (idx) => {
        onChange(items.filter((_, i) => i !== idx));
      };
      return (
        <div>
          <div className={listStyles.arrayList}>
            {items.map((item, idx) => (
              <div key={idx} className={listStyles.arrayRow}>
                <span className={listStyles.arrayIndex}>{idx + 1}</span>
                <input
                  type="text"
                  className={`${inputClassName} ${listStyles.arrayInput}`}
                  value={item || ""}
                  placeholder={`Item ${idx + 1}`}
                  onChange={(e) => updateItem(idx, e.target.value)}
                />
                <button
                  type="button"
                  className={listStyles.arrayRemoveBtn}
                  onClick={() => removeItem(idx)}
                  aria-label={`Remove item ${idx + 1}`}
                >
                  ×
                </button>
              </div>
            ))}
          </div>
          <button type="button" className={listStyles.arrayAddBtn} onClick={addItem} disabled={items.length >= maxItems}>
            + Add {items.length > 0 ? "another" : "item"}
          </button>
          <div className={listStyles.arrayHint}>
            {items.length} of {maxItems} added
          </div>
        </div>
      );
    }

    default:
      return null;
  }
}
